import { Api } from "@/api/Api";
import { ProvideAuth } from "@/auth";
import { AppSessionProvider } from "@/auth/AppSessionContext";
import { registerChunkLoadAutoReload } from "@/chunkLoadAutoReload";
import { ENV } from "@/env";
import { FeatureHubProvider } from "@/feature-flags";
import { getRoot } from "@/renderRoot";
import { sentryBeforeSendEvent, sentryBeforeSendTransaction } from "@/sentry/SentryConfig";
import { Configuration as EnergyConfig } from "@mm/energy.metx.meteomatics.com";
import { Configuration } from "@mm/metx-workbench.meteomatics.com";
import { OpenFeature } from "@openfeature/react-sdk";
import { StrictMode } from "react";
import App from "./App";

if (process.env.REACT_APP_FEATURE_HUB_URL && process.env.REACT_APP_FEATURE_HUB_API_KEY) {
  OpenFeature.setProvider(
    new FeatureHubProvider(process.env.REACT_APP_FEATURE_HUB_URL, process.env.REACT_APP_FEATURE_HUB_API_KEY),
  );
}

async function initSentry() {
  if (process.env.NODE_ENV !== "production" || !process.env.REACT_APP_METX_SENTRY_DSN) {
    return;
  }

  const { globalHandlersIntegration, init, reactRouterV5BrowserTracingIntegration } = await import("@sentry/react");

  init({
    dsn: process.env.REACT_APP_METX_SENTRY_DSN,
    /* Ignore all Non-Errors and me
     * Non-Error is caused by rejected promise, that doesn't return Error instance
     * In this case there is nothing sentry can log out, so best and only way is
     * to just ignore theses "non-errors" and wrote promise rejections correctly.
     */
    ignoreErrors: ["Non-Error exception captured", "Non-Error promise rejection captured"],
    // tracingOrigins is used to pass cors header
    integrations: [
      reactRouterV5BrowserTracingIntegration({
        history,
      }),
      globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],
    tracesSampleRate: Number.parseFloat(process.env.REACT_APP_SENTRY_TRACE_RATE || "0.1"),
    environment: process.env.NODE_ENV,
    beforeSendTransaction: sentryBeforeSendTransaction,
    beforeSend: sentryBeforeSendEvent,
    release: process.env.REACT_APP_SENTRY_RELEASE || "unknown",
  });
}

if (process.env.NODE_ENV === "production") {
  registerChunkLoadAutoReload();
  initSentry();
}

if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_XSTATE_INSPECT === "true") {
  // open inspector in a separate window
  import("@xstate/inspect").then(({ inspect }) => inspect({ iframe: false }));
}

// Create singleton to access API
Api.initialize(
  new Configuration({
    basePath: ENV.baseUrlMetx,
    accessToken: () => {
      const authLocalStorageStr = window.localStorage.getItem("auth");
      if (!authLocalStorageStr) {
        return "";
      }
      const authJson = JSON.parse(authLocalStorageStr);
      if (!authJson || !authJson?.token?.access_token) {
        return "";
      }
      return authJson.token.access_token;
    },
  }),
  new EnergyConfig({
    basePath: ENV.baseUrlEnergyForecast,
    accessToken: () => {
      const authLocalStorageStr = window.localStorage.getItem("auth");
      if (!authLocalStorageStr) {
        return "";
      }
      return JSON.parse(authLocalStorageStr).token.access_token;
    },
  }),
);

getRoot().render(
  <StrictMode>
    <ProvideAuth>
      <AppSessionProvider>
        <App />
      </AppSessionProvider>
    </ProvideAuth>
  </StrictMode>,
);
